import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ImageOff, Truck } from "lucide-react";

const CartPage = () => {
  const { t, lang, cart, updateCartQty, removeFromCart, cartSubtotal, deliveryFee, cartTotal } = useApp();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="empty-cart">
        <ShoppingBag className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="font-heading font-bold text-2xl text-[#1E293B]">{t("emptyCart")}</h2>
        <Link to="/products">
          <Button className="mt-6 rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white h-12 px-6" data-testid="start-shopping-btn">
            {t("startShopping")} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      </div>
    );
  }

  const remaining = Math.max(0, 500 - cartSubtotal);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="cart-page">
      <h1 className={`font-heading font-bold text-3xl text-[#1E293B] mb-6 ${lang === "hi" ? "font-devanagari" : ""}`}>
        {t("yourCart")}
      </h1>

      {deliveryFee > 0 && remaining > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-3" data-testid="free-delivery-hint">
          <Truck className="w-5 h-5 text-amber-700 shrink-0" />
          <p className="text-sm text-amber-900">
            {t("addMoreForFree", { amount: remaining.toFixed(0) })}
          </p>
        </div>
      )}
      {deliveryFee === 0 && cartSubtotal > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-3">
          <Truck className="w-5 h-5 text-emerald-700 shrink-0" />
          <p className="text-sm text-emerald-900 font-medium">{t("freeDeliveryEligible")}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3" data-testid="cart-items-list">
          {cart.map((it) => {
            const name = lang === "hi" ? it.name_hi : it.name_en;
            const img = fileUrl(it.image_path);
            return (
              <div
                key={it.product_id}
                className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4 flex gap-3 sm:gap-4"
                data-testid={`cart-item-${it.product_id}`}
              >
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-[#EFEBE2] shrink-0">
                  {img ? (
                    <img src={img} alt={name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <ImageOff className="w-6 h-6" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-heading font-semibold text-sm sm:text-base text-[#1E293B] line-clamp-2 ${lang === "hi" ? "font-devanagari" : ""}`}>
                    {name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">{it.unit}</p>
                  <p className="font-heading font-bold text-[#2D5A27] mt-1">₹{it.price.toFixed(0)}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="flex items-center border border-[#E2E8F0] rounded-full">
                      <button
                        onClick={() => updateCartQty(it.product_id, it.quantity - 1)}
                        data-testid={`cart-qty-dec-${it.product_id}`}
                        className="w-8 h-8 flex items-center justify-center rounded-l-full hover:bg-[#EFEBE2]"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-9 text-center text-sm font-semibold" data-testid={`cart-qty-${it.product_id}`}>
                        {it.quantity}
                      </span>
                      <button
                        onClick={() => updateCartQty(it.product_id, it.quantity + 1)}
                        data-testid={`cart-qty-inc-${it.product_id}`}
                        className="w-8 h-8 flex items-center justify-center rounded-r-full hover:bg-[#EFEBE2]"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <button
                      onClick={() => removeFromCart(it.product_id)}
                      data-testid={`cart-remove-${it.product_id}`}
                      className="text-slate-400 hover:text-rose-600 p-2 rounded-full hover:bg-rose-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 sticky top-24" data-testid="cart-summary">
            <h3 className="font-heading font-semibold text-lg text-[#1E293B] mb-4">{t("orderSummary")}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">{t("subtotal")}</span>
                <span className="font-semibold" data-testid="cart-subtotal">₹{cartSubtotal.toFixed(0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t("deliveryFee")}</span>
                <span className={`font-semibold ${deliveryFee === 0 ? "text-emerald-600" : ""}`} data-testid="cart-delivery-fee">
                  {deliveryFee === 0 ? "FREE" : `₹${deliveryFee.toFixed(0)}`}
                </span>
              </div>
              <div className="h-px bg-[#E2E8F0] my-2" />
              <div className="flex justify-between text-base">
                <span className="font-heading font-semibold">{t("total")}</span>
                <span className="font-heading font-bold text-[#2D5A27] text-xl" data-testid="cart-total">
                  ₹{cartTotal.toFixed(0)}
                </span>
              </div>
            </div>
            <Button
              onClick={() => navigate("/checkout")}
              data-testid="proceed-checkout-btn"
              className="w-full mt-5 h-12 rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white font-semibold pressable"
            >
              {t("proceedCheckout")} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <p className="mt-3 text-xs text-center text-slate-500">{t("codAvailable")}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
